import { useState, useRef, useLayoutEffect } from 'react'
import styled from 'styled-components'
import { Portal } from './Portal'

const Root = styled.div<{ topOffset: number }>`
  position: absolute;
  top: ${props => props.topOffset}px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${props => props.theme.zIndex.modal};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgb(18 18 18 / 80%);
  transition: all 0.3s ease-in-out;
  overflow: hidden;
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 400px;
  border-radius: 4px;
  overflow: hidden;
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 30px 20px;
  height: 100%;
  color: #FFF;
  overflow-y: auto;
`
const Footer = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 15px;
  padding: 20px;
`

interface DialogProps {
  title: string | React.ReactNode
  children: React.ReactNode
  onClose: () => void
  subTitle?: string
  style?: React.CSSProperties
  clickOutside?: boolean
  // portal wrapper id — dialogs that can be open at the same time must use distinct
  // ids, otherwise unmounting one tears down the shared portal node of the other.
  id?: string
  // pixels to offset the overlay's top edge by (e.g. to leave a header uncovered)
  topOffset?: number
}

function Dialog({ children, title, onClose, subTitle, clickOutside = true, style, id = 'dialog_portal', topOffset = 0 }: DialogProps) {
  if (subTitle && !title) {
    console.error('title prop must be passed')
  }

  const [isVisible, setIsVisible] = useState(false)

  const rootRef = useRef<HTMLDivElement>(null)

  // Close on backdrop click. We check the Root overlay itself (not a global
  // document listener) so that a click inside a stacked dialog rendered in a
  // DIFFERENT portal does NOT count as an outside-click for this one.
  const onBackdropMouseDown = (e: React.MouseEvent) => {
    if (clickOutside && e.target === rootRef.current) onClose()
  }

  useLayoutEffect(() => {
    const closeOnEscapeKey = (e: KeyboardEvent) => e.key === 'Escape' ? onClose() : null
    document.body.addEventListener('keydown', closeOnEscapeKey)

    setTimeout(() => {
      setIsVisible(true)
    }, 300)

    return () => {
      document.body.removeEventListener('keydown', closeOnEscapeKey)
    }
  }, [onClose])

  return (
    <Portal wrapperId={id}>
      <Root ref={rootRef} topOffset={topOffset} style={{ opacity: isVisible ? 1 : 0 }} onMouseDown={onBackdropMouseDown}>
        <Container style={style}>
          {children}
        </Container>
      </Root>
    </Portal>
  )
}

interface CommonProps {
  style?: React.CSSProperties
}

Dialog.Content = ({ children, style }: React.PropsWithChildren<CommonProps>) => (
  <Content style={style}>
    {children}
  </Content>
)
Dialog.Footer = ({ children, style }: React.PropsWithChildren<CommonProps>) => (
  <Footer style={style}>
    {children}
  </Footer>
)

export { Dialog }